import React, { useContext, useState, useMemo, useEffect, useRef } from "react";
import { CartContext } from "../CartContext";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { useTranslation } from "react-i18next";
import emailjs from "@emailjs/browser";

const Checkout = () => {
  const cart = useContext(CartContext);
  const navigate = useNavigate();
  const [selectedCountry, setSelectedCountry] = useState("Kosovo");
  const [shippingPrice, setShippingPrice] = useState(0);
  const [paidFor, setPaidFor] = useState(false);
  const [error, setError] = useState(null);
  const { t } = useTranslation();
  const formRef = useRef();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    document.title = "Checkout | BP11 ArtPrints";
  }, []);

  // Form fields state
  const [formData, setFormData] = useState({
    name: "",
    phoneNumber: "",
    city: "",
    address: "",
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleShippingChange = (e) => {
    const selectedOption = e.target.value;
    setSelectedCountry(selectedOption);
    setShippingPrice(0); // Always free

    const dialCode = countryDialCodes[selectedOption] || "";

    setFormData((prev) => {
      // If phoneNumber already starts with dial code, don’t add again
      if (prev.phoneNumber.startsWith(dialCode)) {
        return prev;
      }
      // Otherwise prefix dial code
      return { ...prev, phoneNumber: dialCode };
    });
  };

  const calculateSubtotal = useMemo(() => {
    if (!cart.items) {
      return 0;
    }
    return cart.items.reduce(
      (total, item) => total + item.price * item.quantity,
      0
    );
  }, [cart.items]);

  const calculateTotalPrice = useMemo(() => {
    return calculateSubtotal + shippingPrice;
  }, [calculateSubtotal, shippingPrice]);

  const sendEmail = (e) => {
    e.preventDefault();

    if (!isPhoneNumberValid()) {
      alert(
        `Please enter a valid phone number with at least 7 digits after the country code (${countryDialCodes[selectedCountry]}).`
      );
      return;
    }

    emailjs
      .sendForm(
        "service_66nv3yp",
        "template_kr8zhpc",
        formRef.current,
        "IuabpnC5ucjHM8RBh"
      )
      .then(
        (result) => {
          // alert("Email sent successfully!");
          navigate("/success-page");
          formRef.current.reset();
        },
        (error) => {
          console.error("Email error:", error.text);
          alert("Failed to send. Try again.");
        }
      );
  };

  const countryDialCodes = {
    Kosovo: "+383",
    Albania: "+355",
    "North Macedonia": "+389",
    Germany: "+49",
    France: "+33",
    Italy: "+39",
    Spain: "+34",
    Switzerland: "+41",
    Austria: "+43",
    Greece: "+30",
    Netherlands: "+31",
    Belgium: "+32",
    Sweden: "+46",
    Norway: "+47",
    Finland: "+358",
    Denmark: "+45",
    Ireland: "+353",
    Portugal: "+351",
    Poland: "+48",
    "Czech Republic": "+420",
    Hungary: "+36",
    Romania: "+40",
    Slovenia: "+386",
    Croatia: "+385",
    Bulgaria: "+359",
    serbi: "+381",
    Montenegro: "+382",
    "Bosnia and Herzegovina": "+387",
    "United Kingdom": "+44",
    "United States": "+1",
    Canada: "+1",
  };

  const isPhoneNumberValid = () => {
    const dialCode = countryDialCodes[selectedCountry] || "";
    const phone = formData.phoneNumber || "";

    // Remove the dial code from the start if present
    let numberWithoutCode = phone.startsWith(dialCode)
      ? phone.slice(dialCode.length)
      : phone;

    // Remove all non-digit characters (spaces, dashes, etc.)
    numberWithoutCode = numberWithoutCode.replace(/\D/g, "");

    // Check if at least 7 digits remain
    return numberWithoutCode.length >= 7;
  };

  return (
    <Container>
      {cart.items.length === 0 ? (
        <p>Your cart is empty.</p>
      ) : (
        <>
          <div className="left">
            {cart.items.map((item) => (
              <div className="item" key={item.id}>
                <img src={item.image} alt="" />
                <span>
                  <h3>{item.title}</h3>
                  <h4>Quantity: {item.quantity}</h4>
                  <h4>Size: {item.selectedSize}</h4>
                  <h4>Canvas Type: {item.canvasType}</h4>
                  <h3>Price: {item.price}€</h3>
                </span>
              </div>
            ))}
            <div style={{ display: "none" }}>
              {cart.items.map((item, index) => (
                <div key={index}>
                  <input
                    name={`product_${index}_title`}
                    defaultValue={item.title}
                  />
                  <input
                    name={`product_${index}_quantity`}
                    defaultValue={item.quantity}
                  />
                  <input
                    name={`product_${index}_size`}
                    defaultValue={item.selectedSize}
                  />
                  <input
                    name={`product_${index}_canvasType`}
                    defaultValue={item.canvasType}
                  />
                  <input
                    name={`product_${index}_price`}
                    defaultValue={`${item.price}€`}
                  />
                </div>
              ))}
              <input name="shippingPrice" defaultValue={`${shippingPrice}€`} />
              <input
                name="totalPrice"
                defaultValue={calculateTotalPrice.toFixed(2)}
              />
            </div>

            <div className="total">
              <span>
                <h3>Subtotal</h3>
                <h2>€{calculateSubtotal.toFixed(2)}</h2>
              </span>
              <span>
                <h3>Transporti</h3>
                {shippingPrice === 0 ? (
                  <h2>FALAS</h2>
                ) : (
                  <h2>€{shippingPrice.toFixed(2)}</h2>
                )}
              </span>
              <hr />
              <span>
                <h2>Totali</h2>
                <h1>€{calculateTotalPrice.toFixed(2)}</h1>
              </span>
            </div>
          </div>
          <hr />
          <form className="right" ref={formRef} onSubmit={sendEmail}>
            {cart.items.map((item, index) => (
              <div key={index} style={{ display: "none" }}>
                <input
                  type="text"
                  name={`product_${index}_title`}
                  value={item.title}
                  readOnly
                />
                <input
                  type="text"
                  name={`product_${index}_quantity`}
                  value={item.quantity}
                  readOnly
                />
                <input
                  type="text"
                  name={`product_${index}_size`}
                  value={item.selectedSize}
                  readOnly
                />
                <input
                  type="text"
                  name={`product_${index}_canvasType`}
                  value={item.canvasType}
                  readOnly
                />
                <input
                  type="text"
                  name={`product_${index}_price`}
                  value={`${item.price}€`}
                  readOnly
                />
              </div>
            ))}
            <p>
              <b>Note:</b>
              {t("paymentNote")}
            </p>
            <div className="contact">
              <label>{t("deliveryDetails")}</label>
              <div className="delivery">
                <input
                  name="name"
                  type="text"
                  placeholder={t("fullName")}
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
                <span>
                  <input
                    type="text"
                    name="phoneNumber"
                    placeholder={t("phoneNumber")}
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    minLength={7}
                    required
                  />
                  <select
                    onChange={handleShippingChange}
                    name="country"
                    required
                  >
                    <option value="Kosovo">Kosovo</option>
                    <option value="Albania">Albania</option>
                    <option value="North Macedonia">North Macedonia</option>
                    <option value="Germany">Germany</option>
                    <option value="France">France</option>
                    <option value="Italy">Italy</option>
                    <option value="Spain">Spain</option>
                    <option value="Switzerland">Switzerland</option>
                    <option value="Austria">Austria</option>
                    <option value="Greece">Greece</option>
                    <option value="Netherlands">Netherlands</option>
                    <option value="Belgium">Belgium</option>
                    <option value="Sweden">Sweden</option>
                    <option value="Norway">Norway</option>
                    <option value="Finland">Finland</option>
                    <option value="Denmark">Denmark</option>
                    <option value="Ireland">Ireland</option>
                    <option value="Portugal">Portugal</option>
                    <option value="Poland">Poland</option>
                    <option value="Czech Republic">Czech Republic</option>
                    <option value="Hungary">Hungary</option>
                    <option value="Romania">Romania</option>
                    <option value="Slovenia">Slovenia</option>
                    <option value="Croatia">Croatia</option>
                    <option value="Bulgaria">Bulgaria</option>
                    <option value="serbi">serbi</option>
                    <option value="Montenegro">Montenegro</option>
                    <option value="Bosnia and Herzegovina">
                      Bosnia and Herzegovina
                    </option>
                    <option value="United Kingdom">United Kingdom</option>
                    <option value="United States">United States</option>
                    <option value="Canada">Canada</option>
                  </select>
                </span>
                <span>
                  <input
                    name="city"
                    type="text"
                    placeholder={t("city")}
                    value={formData.city}
                    onChange={handleInputChange}
                    required
                  />
                  <input
                    name="address"
                    type="text"
                    placeholder={t("address")}
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                  />
                </span>
                <textarea
                  name="message"
                  cols="10"
                  rows="4"
                  placeholder={t("Message")}
                  onChange={handleInputChange}
                ></textarea>
                <input
                  type="hidden"
                  name="totalPrice"
                  value={calculateTotalPrice.toFixed(2)}
                />
              </div>
            </div>
            <button type="Submit">{t("orderNow")}</button>
            <textarea
              name="orderSummary"
              style={{ display: "none" }}
              value={
                cart.items
                  .map(
                    (item) =>
                      `${item.quantity}x ${item.title} - ${item.selectedSize} - ${item.canvasType} - €${item.price}`
                  )
                  .join("\n") +
                `\n\nTransporti: ${
                  shippingPrice === 0 ? "FALAS" : `${shippingPrice.toFixed(2)}`
                }€` +
                `\nTotali: €${calculateTotalPrice.toFixed(2)}`
              }
            />
          </form>
          <div style={{ display: "none" }}>
            {cart.items.map((item, index) => (
              <div key={index}>
                <input
                  name={`product_${index}_title`}
                  defaultValue={item.title}
                />
                <input
                  name={`product_${index}_quantity`}
                  defaultValue={item.quantity}
                />
                <input
                  name={`product_${index}_size`}
                  defaultValue={item.selectedSize}
                />
                <input
                  name={`product_${index}_canvasType`}
                  defaultValue={item.canvasType}
                />
                <input
                  name={`product_${index}_price`}
                  defaultValue={`${item.price}€`}
                />
              </div>
            ))}
            <input name="shippingPrice" defaultValue={`${shippingPrice}€`} />
            <input
              name="totalPrice"
              defaultValue={calculateTotalPrice.toFixed(2)}
            />
          </div>
        </>
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 7em 7%;
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 7%;
  margin: auto;
  max-width: 1500px;
  background-color: var(--primaryBackgroundColor);
  color: var(--fontPrimaryColor);
  hr {
    height: 70vh;
    border: 1px solid var(--secondaryBackgroundColor);
  }
  .left {
    display: flex;
    flex-direction: column;
    align-items: end;
    flex: 3;
    .item {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      width: 100%;
      gap: 10px;
      padding: 10px;
      border-bottom: 1px solid var(--secondaryBackgroundColor);
      width: 100%;
      text-align: end;
      img {
        height: 150px;
        width: auto;
        border-radius: 0px;
        object-fit: contain;
        background-color: white;
      }
      span {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: start;
        width: 100%;
        h4 {
          text-transform: capitalize;
          font-size: 17px;
          font-weight: 300;
          color: var(--fontSecondaryColor);
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 7px;
          span {
            color: #3f3f3f;
            font-weight: 500;
          }
        }
        h3 {
          text-transform: capitalize;
          font-weight: 500;
          color: var(--fontPrimaryColor);
        }
        h2 {
          margin-top: 10px;
          font-weight: 500;
          color: #747474;
        }
      }
    }
    .total {
      display: flex;
      flex-direction: column;
      gap: 10px;
      width: 100%;
      margin-top: 3em;
      hr {
        border: none;
        height: 1px;
        border-bottom: 1px solid var(--fontSecondaryColor);
      }
      span {
        display: flex;
        justify-content: space-between;
        h3 {
          font-weight: 400;
          color: var(--fontSecondaryColor);
        }
        h2 {
          font-weight: 500;
          font-size: 19px;
          color: #929292;
        }
        h1 {
          font-size: 20px;
          font-weight: 600;
          color: var(--fontPrimaryColor);
        }
      }
    }
  }
  .right {
    margin-top: 2%;
    flex: 4;
    display: flex;
    flex-direction: column;
    gap: 2em;
    p {
      color: var(--fontSecondaryColor);
    }
    div {
      display: flex;
      flex-direction: column;
      gap: 7px;
    }
    span {
      font-weight: 400;
      display: flex;
      align-items: center;
      gap: 10px;
    }
    input,
    textarea,
    select {
      font-family: "Montserrat", sans-serif;
      outline: none;
      border: 1px solid var(--fontThirdColor);
      font-size: 17px;
      border-radius: 7px;
      padding: 14px;
      background: none;
      color: gray;
      width: 100%;
    }
    label {
      font-weight: 500;
      font-size: 20px;
    }
    .shipping {
      background-color: var(--secondaryBackgroundColor);
      padding: 20px 30px;
      border-radius: 30px;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;

      span {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
        h3 {
          font-size: 20px;
          font-weight: 500;
        }
        h4 {
          font-weight: 300;
        }
      }
    }
    button {
      margin: auto;
      font-family: "Poppins", sans-serif;
      font-size: 18px;
      background: #278527c1;
      color: white;
      border: none;
      padding: 10px 14px;
      cursor: pointer;
      transition: 0.4s;
      margin: 10px;
      &:hover {
        background: #1b691bc1;
      }
    }
  }
  @media (max-width: 800px) {
    flex-direction: column;
    gap: 2em;
    hr {
      height: 1px;
    }
    .left {
      .item {
        img {
          width: 45%;
        }
      }
      .total {
      }
    }
  }
`;

export default Checkout;
